import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { landingUrls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { FetchActivityResponse, RecentActivityItem } from 'types/landing';
import {
    canOpenActivity,
    getStoredActivitySessionId,
    resolveRecentActivityLimit,
    toRecentActivityItem,
} from 'utils/recentActivity';

const mapActivities = (response: FetchActivityResponse | undefined): RecentActivityItem[] =>
    (response?.activities || []).map(toRecentActivityItem).filter((item): item is RecentActivityItem => {
        if (!item) return false;
        return canOpenActivity(item.pageId);
    });

export function useFetchRecentActivity() {
    const { appConfig } = useContext(UserContext);
    const sessionId = getStoredActivitySessionId();
    const limit = resolveRecentActivityLimit(appConfig);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['rgm-recent-activity', sessionId, limit],
        enabled: Boolean(sessionId),
        queryFn: async (): Promise<FetchActivityResponse> => {
            const result = await axios.get(landingUrls.fetchActivityUrl, {
                params: { session_id: sessionId, limit },
            });
            return result.data;
        },
        staleTime: 60 * 1000,
    });

    return {
        activities: mapActivities(data),
        isLoading,
        isError,
    };
}

export default useFetchRecentActivity;
